import React from 'react';
import { Link } from 'react-router-dom';

const CFOs = () => {
  const outcomes = [
    { value: '30–50%', label: 'Budget variance ↓', desc: 'Predict PMPM and high-cost cohorts early. Course-correct before surprises hit the P&L.' },
    { value: '5–12%', label: 'Medical savings', desc: 'Target price/usage outliers, optimize specialty pharmacy, and renegotiate with proof.' },
    { value: 'Clicks', label: 'Audit-ready in', desc: 'Traceable assumptions, automated decks, and benchmarks ready for the board and auditors.' }
  ];

  const useCases = [
    { icon: '💰', title: 'Spend Governance', desc: 'Real-time PMPM by plan, site, and condition. Track price × utilization, leakage, and rebates.' },
    { icon: '📊', title: 'Rolling Forecasting', desc: 'Scenario models for trend, inflation, mix, and plan design. Lock budgets with sensitivity bands.' },
    { icon: '📑', title: 'Contract & Vendor ROI', desc: 'Compare outcomes vs. cost, surface waste, and generate negotiation briefs with playbooks.' },
    { icon: '💊', title: 'Specialty Drug Oversight', desc: 'Indication-level tracking, site-of-care shifts, biosimilar adoption, and rebate optimization.' },
    { icon: '⚖️', title: 'Risk & Compliance', desc: 'Automated checks for ACA/FMLA, parity, and plan changes—with alerts and recommended actions.' },
    { icon: '📈', title: 'Board & EBITDA Reporting', desc: 'One-click decks: variance, drivers, mitigations, and EBITDA impact with clear narratives.' }
  ];

  const steps = [
    { num: '1', title: 'Connect data sources', desc: 'Claims, HRIS, PBM, TPA, finance extracts. We normalize and map to a consistent model.' },
    { num: '2', title: 'Continuous analysis', desc: 'AI workers monitor drivers, spot anomalies, and benchmark vendors 24/7.' },
    { num: '3', title: 'Decisions & controls', desc: 'Auto-generated levers: plan design, site of care, steerage, formulary, contract terms.' },
    { num: '4', title: 'Share & govern', desc: 'Live dashboards and board-ready decks with traceable assumptions and data lineage.' }
  ];

  return (
    <div className="page-content">
      {/* Hero */}
      <section className="hero">
        <div className="hero-content">
          <p className="hero-subtitle">Solutions for CFOs</p>
          <h1 className="hero-title">
            Tame Health Spend. Improve EBITDA. <span className="gradient-text">Forecast with Confidence.</span>
          </h1>
          <p className="hero-description">
            Avenir AI gives Finance leaders real-time visibility into medical trend, vendor ROI, and risk—so you can lock budgets, reduce volatility, and brief the board with evidence.
          </p>
          <div className="hero-buttons">
            <Link to="/contact" className="btn btn-primary btn-large">Talk to a Specialist</Link>
            <a href="#how-it-works" className="btn btn-secondary btn-large">How it Works</a>
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="section">
        <div className="section-center" style={{ marginBottom: '4rem' }}>
          <h2 className="section-heading">
            Finance outcomes you can <span className="gradient-text">measure</span>
          </h2>
        </div>

        <div className="outcomes-grid">
          {outcomes.map((outcome, i) => (
            <div key={i} className="outcome-card">
              <div className="outcome-value">{outcome.value}</div>
              <div className="outcome-label">{outcome.label}</div>
              <p className="outcome-desc">{outcome.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Use Cases */}
      <section className="section">
        <div className="section-center" style={{ marginBottom: '4rem' }}>
          <h2 className="section-heading">
            What Finance teams use <span className="gradient-text">Avenir AI</span> for
          </h2>
        </div>

        <div className="use-cases-grid">
          {useCases.map((useCase, i) => (
            <div key={i} className="use-case-card">
              <div className="use-case-icon">{useCase.icon}</div>
              <h3 className="use-case-title">{useCase.title}</h3>
              <p className="use-case-desc">{useCase.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works */}
      <section className="section" id="how-it-works">
        <div className="section-center" style={{ marginBottom: '4rem' }}>
          <h2 className="section-heading">
            How it <span className="gradient-text">works</span>
          </h2>
        </div>

        <div className="steps-grid">
          {steps.map((step, i) => (
            <div key={i} className="step-card">
              <div className="step-num">{step.num}</div>
              <h3 className="step-title">{step.title}</h3>
              <p className="step-desc">{step.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Ecosystem */}
      <section className="section">
        <div className="section-center">
          <h2 className="section-heading">
            Fits your finance & HR <span className="gradient-text">stack</span>
          </h2>
          <p className="section-description">
            Plug into HRIS, PBM, TPA, data warehouses—or upload flat files. SSO and role-based access supported.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="section">
        <div className="cta-wrapper">
          <div className="cta-glow"></div>
          <div className="cta-box">
            <h2 className="cta-title">See Avenir AI for Finance</h2>
            <p className="cta-desc">Learn how CFO teams stabilized trend and delivered measurable savings—fast.</p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn btn-primary btn-large">Book a Demo</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CFOs;